import { ApolloError } from "@apollo/client";
import { useEffect, useState } from "react";

import { useAppContext } from "@/app/context/app-context";
import { formatRecOptions, RECOrderAttributeOptions } from "@/app/lib/format-rec-options";
import { AccountStatus, AssetType, useAssetByAssetCodeQuery } from "@/app/types/generated/graphql";
import { AggregatedRegistryCode } from "@/app/types/okta.types";

import { useAllFeatureToggleValues } from "../use-feature-toggle/use-all-feature-toggle-values";

type UseRecOptionProps = {
  /**
   * `registryCode` can be `ERCOT` | `MRETS` | `null` when used in Forward feature; otherwise, it will be `undefined` - therefore should respect value from global app context: `userSelections`
   */
  registryCode?: AggregatedRegistryCode;
};

export const useRecOptions = ({ registryCode }: UseRecOptionProps = {}) => {
  const [formattedRecOptions, setFormattedRecOptions] = useState<RECOrderAttributeOptions>();
  const { userAccountInfo, userSelections } = useAppContext();
  const [accumulatedErrors, setAccumulatedErrors] = useState<ApolloError | Error | undefined>();

  const {
    toggles: { enableCrossRegistryForwardTrading },
    loading: featureToggleLoading,
  } = useAllFeatureToggleValues();

  const { data, loading: assetsLoading } = useAssetByAssetCodeQuery({
    skip: !userAccountInfo?.tenantId || userAccountInfo?.status !== AccountStatus.Active || featureToggleLoading,
    variables: {
      assetCode: AssetType.Rec,
      ...(enableCrossRegistryForwardTrading ? {} : { registryCode: userSelections?.registry?.registryCode }),
    },
    onError: (error) => {
      setAccumulatedErrors(error);
    },
  });

  useEffect(() => {
    if (data?.assetByAssetCode) {
      try {
        const assets = enableCrossRegistryForwardTrading
          ? {
              ...data.assetByAssetCode,
              attributes: data.assetByAssetCode?.attributes?.filter(
                (attribute) =>
                  attribute.registryCode ===
                  (registryCode !== undefined ? registryCode : userSelections?.registry?.registryCode),
              ),
            }
          : data.assetByAssetCode;

        const formattedOptions = formatRecOptions(assets);
        setFormattedRecOptions(formattedOptions);
      } catch (error) {
        if (error instanceof Error) {
          setAccumulatedErrors(error);
        }
      }
    }
  }, [data?.assetByAssetCode, enableCrossRegistryForwardTrading, registryCode, userSelections?.registry?.registryCode]);

  if (accumulatedErrors) {
    throw accumulatedErrors;
  }

  return {
    recOptions: formattedRecOptions,
    loading: assetsLoading || !userAccountInfo?.loaded || featureToggleLoading,
    error: accumulatedErrors,
  };
};
